import * as React from 'react'
import { useCallback } from 'react'
import { useQueryClient } from 'react-query'
import { useFormikContext } from 'formik'
import { OpportunityFilterSet } from '@user/store/opportunity/filters'
import { IFilterMode } from '@user/components/Filters/typings/IFilterMode'
import {
  DateRangeFilter,
  Divider,
  IOption,
  MultiSelectFilter,
  NullableFilter,
  TernaryFilter,
  ValueRangeFilter,
} from '@user/components/Filters'
import { ServiceProvider } from './ServiceProvider'
import { SalesFunnelProvider } from './SalesFunnelProvider'
import { SalesFunnelStageProvider } from './SalesFunnelStageProvider'
import { CreatedByProvider } from './CreatedByProvider'
import { PostalCodeProvider } from './PostalCodeProvider'
import { BillToPostalCodeProvider } from './BillToPostalCodeProvider'
import { LocationTypeProvider } from './LocationTypeProvider'
import { DivisionProvider } from './DivisionProvider'
import { SalesTeamProvider } from './SalesTeamProvider'
import { SourceProvider } from './SourceProvider'
import { CampaignProvider } from './CampaignProvider'
import { SoldServiceStatusesProvider } from './SoldServiceStatusesProvider'
import { ActivityTypesContextProvider } from '@user/features/Activity/ActivitiyTypes/context'
import { ActivityTypeProvider } from '@user/components/Leads/LeadFilters/ActivityTypeProvider'
import { ActivityStatusProvider } from '@user/components/Leads/LeadFilters/ActivityStatusProvider'
import { AssigneeProvider } from '@user/components/Leads/LeadFilters/AssigneeProvider'
import { MultiSelectTidalFilter } from '@user/components/Filters/components/MultiSelectFilter'
import { LeadTypeProvider } from '@user/components/Leads/LeadFilters'
import { useTranslation } from 'react-i18next'
import { ImportNameProvider } from './ImportNameProvider'
import { SubmittedByProvider } from '@user/components/Opportunities/OpportunityFilters/SubmittedByProvider'
import { TargetPestsProvider } from './TargetPestsProvider'
import { AgreementStatusProvider } from '@user/components/Leads/LeadFilters/AgreementStatusProvider'
import { KnockStatusFilter } from '@user/components/Filters/components/KnockStatusFilter'
import { useKnockStatusFilterEnabled } from '@user/store/tenantFeatures/hooks/useKnockStatusFilterEnabled'

const filterSalesFunnelStageIdDefaultValue = { mode: 'any', options: [] }

export const OpportunityFilters: React.FC<IFilterMode> = (props) => {
  const {
    values: {
      filters: {
        salesFunnelId: { value: salesFunnelId },
        salesTeamIds: { value: salesTeamIds },
        salesFunnelStageId,
      },
    },
    setFieldValue,
  } = useFormikContext<OpportunityFilterSet>()
  const { t } = useTranslation()
  const client = useQueryClient()
  const { reset } = props
  const knockStatusFilterEnabled = useKnockStatusFilterEnabled()

  React.useEffect(() => {
    client.removeQueries(['filters', 'opp'])
    client.removeQueries(['filters', 'shared'])
  }, [client])

  const isOptionEqual = useCallback(
    (optionA: IOption, optionB: IOption) => optionA.value === optionB.value,
    [],
  )

  React.useEffect(() => {
    if (salesFunnelId?.options?.length == 0) {
      setFieldValue('filters.salesFunnelStageId', {
        ...salesFunnelStageId,
        value: filterSalesFunnelStageIdDefaultValue,
      })
    }
  }, [salesFunnelId, setFieldValue])

  return (
    <>
      <DateRangeFilter
        name="createdDate"
        label="Opportunity Created Date"
        disableFuture
        reset={reset}
      />
      <DateRangeFilter
        name="leadCreatedTime"
        label="Lead Created Date"
        disableFuture
        reset={reset}
      />
      <DateRangeFilter
        name="closedDate"
        label="Closed Date"
        disableFuture
        reset={reset}
      />
      <DateRangeFilter
        name="nextActionDate"
        label="Next Action Date"
        reset={reset}
      />
      <DateRangeFilter
        name="expectedClosingDate"
        label="Expected Closing Date"
        reset={reset}
      />

      <Divider />

      <MultiSelectTidalFilter
        name="salesTeamIds"
        label="Sales Team"
        component={SalesTeamProvider}
        isOptionEqual={isOptionEqual}
        hideCheckBox={false}
      />

      <MultiSelectTidalFilter
        name="assignee"
        label="Owner"
        component={AssigneeProvider}
        isOptionEqual={isOptionEqual}
        hideCheckBox={false}
        reset={reset}
        dependsOn={salesTeamIds.options}
      />

      <MultiSelectTidalFilter
        name="leadCreatedBy"
        label="Lead Created By"
        component={CreatedByProvider}
        isOptionEqual={isOptionEqual}
        hideCheckBox={false}
        reset={reset}
      />

      <MultiSelectTidalFilter
        name="createdBy"
        label="Opportunity Created By"
        component={CreatedByProvider}
        isOptionEqual={isOptionEqual}
        hideCheckBox={false}
        reset={reset}
      />

      <MultiSelectFilter
        name="submittedBy"
        label="Submitted By"
        component={SubmittedByProvider}
        reset={reset}
      />

      <Divider />

      <NullableFilter
        name="closed"
        label="Opportunity Status"
        notNullLabel="Closed"
        nullLabel="Open"
        emptyLabel="All"
        reset={reset}
      />

      <Divider />

      <TernaryFilter
        name="hasPestPacLocation"
        label="Has PestPac Location"
        trueLabel="Yes"
        falseLabel="No"
        emptyLabel="All"
        reset={reset}
      />

      <Divider />

      <MultiSelectFilter
        name="soldServiceStatus"
        label="Closed/Won Status"
        component={SoldServiceStatusesProvider}
        reset={reset}
      />

      <Divider />

      <MultiSelectFilter
        name="agreementStatuses"
        label="Agreement Status"
        component={AgreementStatusProvider}
        reset={reset}
      />

      <Divider />

      <MultiSelectTidalFilter
        name="salesFunnelId"
        label="Sales Funnel"
        component={SalesFunnelProvider}
        isOptionEqual={isOptionEqual}
        hideCheckBox={false}
      />

      <MultiSelectTidalFilter
        name="salesFunnelStageId"
        label="Funnel Stages"
        component={SalesFunnelStageProvider}
        isOptionEqual={isOptionEqual}
        hideCheckBox={false}
        reset={reset}
        dependsOn={salesFunnelId.options}
      />
      <Divider />

      <MultiSelectFilter
        name="services"
        label="Services"
        component={ServiceProvider}
        reset={reset}
      />

      <Divider />

      <MultiSelectFilter
        name="targetPests"
        label="Target Pests"
        component={TargetPestsProvider}
        reset={reset}
      />

      <Divider />

      <ActivityTypesContextProvider>
        <MultiSelectFilter
          name="activityTypeId"
          label="Activity Type"
          component={ActivityTypeProvider}
          reset={reset}
        />
      </ActivityTypesContextProvider>

      <MultiSelectFilter
        name="activityStatus"
        label="Activity Status"
        component={ActivityStatusProvider}
        reset={reset}
      />
      <Divider />

      <ValueRangeFilter name="totalValue" label="Total Value" reset={reset} />
      <ValueRangeFilter
        name="initialValue"
        label="Initial Value"
        reset={reset}
      />
      <ValueRangeFilter
        name="recurringValue"
        label="Recurring Value"
        reset={reset}
      />

      <Divider />

      <MultiSelectFilter
        name="locationTypeId"
        label="Location Type"
        component={LocationTypeProvider}
        reset={reset}
      />
      <MultiSelectFilter
        name="divisionId"
        label="Division"
        component={DivisionProvider}
        reset={reset}
      />

      <Divider />

      <MultiSelectFilter
        name="sourceId"
        label="Source"
        component={SourceProvider}
        reset={reset}
      />
      <MultiSelectFilter
        name="campaignId"
        label="Campaign"
        component={CampaignProvider}
        reset={reset}
      />
      <Divider />
      <MultiSelectFilter
        name="type"
        label="Type"
        component={LeadTypeProvider}
        reset={reset}
      />
      <Divider />

      <MultiSelectFilter
        name="postalCode"
        label={t('serviceLocationCode')}
        component={PostalCodeProvider}
        reset={reset}
      />

      <Divider />

      <MultiSelectFilter
        name="billToPostalCode"
        label={t('billToCode')}
        component={BillToPostalCodeProvider}
        reset={reset}
      />
      <Divider />
      <MultiSelectFilter
        name="importId"
        label="Import Name"
        component={ImportNameProvider}
        reset={reset}
      />

      {knockStatusFilterEnabled && (
        <>
          <Divider />

          <MultiSelectTidalFilter
            name="knockStatusSettingId"
            label="Knock Status"
            component={KnockStatusFilter}
            reset={reset}
            hideCheckBox={false}
            isOptionEqual={isOptionEqual}
          />
        </>
      )}
    </>
  )
}
OpportunityFilters.displayName = 'OpportunityFilters'
